import { sunboundPayProxy } from '@proxies';
import { useEffect, useState, useMemo } from "react";
import { Toggle, PaymentMethodCard, ResidentSelector, IconButton, Button } from "@common";
import { useSpinnerContext } from '@contexts';
import { PaymentDay } from ".";
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { Constants, helpers, Enums } from "@shared";
import { useSessionContext } from '@contexts';
import type { PaymentMethod } from "@app-types";
import { useAllowedPaymentTypes } from '@hooks';
import { ChevronLeftIcon } from "@icons";
import { Plus } from "lucide-react";
import classNames from "classnames";
import { PostHogEvents, capturePosthogEvent } from '@analytics';

interface PaymentSettings {
    isAutoPay: boolean;
    dueDay: number | null;
    hasPOTInvoice?: boolean;
    paymentMethods: PaymentMethod[];
}

interface LocationState {
    onClickPaymentFlow?: [string, Record<string, unknown>, Record<string, unknown>];
}

const PaymentMethodSettings = () => {

    const navigate = useNavigate(), location = useLocation() as Location<LocationState | null>;
    const [settings, setSettings] = useState<PaymentSettings | null>(null);
    const { setSpinnerShow } = useSpinnerContext();

    const {
        session,
        setSession,
        accounts,
        selectedAccount
    } = useSessionContext();

    const { orgDays, idAccount, potDeadline } = session || {};

    const { allowedPaymentTypes, isLoading: allowedPaymentTypesIsLoading } = useAllowedPaymentTypes();

    const hasMultipleAccounts = accounts.length > 1;

    useEffect(() => {

        (async () => {
            if (!idAccount) return;
            setSpinnerShow(true);
            try {
                const settings = await sunboundPayProxy.getPaymentSettings(idAccount);
                setSettings(settings);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, [idAccount]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleAutoPay = async () => {
        if (!settings || !idAccount || !orgDays) return;

        setSpinnerShow(true);
        try {
            let { dueDay } = settings;
            const { isAutoPay } = settings;
            const flipAutoPay = !isAutoPay;

            if (flipAutoPay) {
                ({ autodrawDay: dueDay } = orgDays);
            } else
                dueDay = null;

            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { isAutoPay: flipAutoPay, dueDay });
            setSettings({ ...settings, isAutoPay: flipAutoPay, dueDay });
            capturePosthogEvent(PostHogEvents.AUTOPAY_TOGGLED, { enabled: flipAutoPay });

        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const dueDayChange = async (dueDay: number) => {
        if (!settings || !idAccount) return;

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { dueDay });
            setSettings({ ...settings, dueDay });
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const resolveDefaults = selectedAccount
        ? helpers.shouldResolveDefaults(accounts, selectedAccount)
        : false;

    const addDefaultPaymentMethod = () => {
        if (location.state?.onClickPaymentFlow) {
            // in the case where we're resolving a disabled payment
            // we want to use the autopay flow here not just addDefault
            helpers.navigateStart(location, navigate, ...location.state.onClickPaymentFlow);
        } else {
            helpers.navigateStart(location, navigate, Constants.Flows.addDefaultPaymentMethod.id, {
                resolveDefaults
            }, {
                account: { ...session },
                hasMultipleAccounts: false
            });
        }
    };

    const deactivatePaymentMethod = async (idPaymentMethod: number) => {
        if (!idAccount) return;
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.deactivatePaymentMethod(idAccount, idPaymentMethod);
            const deletedMethod = settings?.paymentMethods?.find(pm => pm.idPaymentMethod === idPaymentMethod);
            const [session, newSettings] = await Promise.all([
                sunboundPayProxy.refreshSession(),
                sunboundPayProxy.getPaymentSettings(idAccount)
            ]);
            setSession(session);
            setSettings(newSettings);
            capturePosthogEvent(PostHogEvents.PAYMENT_METHOD_DELETED, {
                paymentType: deletedMethod?.idPaymentType
            });
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const makeDefault = async (idPaymentMethod: number) => {
        if (!idAccount) return;
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.upsertAccountDefaultPaymentMethod(idAccount, idPaymentMethod);
            const [session, settings] = await Promise.all([
                sunboundPayProxy.refreshSession(),
                sunboundPayProxy.getPaymentSettings(idAccount)
            ]);
            // Ensure other pages can retrieve the new default payment method
            // from the session data; the session data should not be stale
            setSession(session);
            // Also update this page to reflect the new default selection
            setSettings(settings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    // find the defaultPM for the currently selected account
    const defaultPM: PaymentMethod | null = useMemo(() => settings?.paymentMethods?.find(pm =>
            pm.accountPaymentMethods?.some(apm => apm.isDefault && apm.accountPayor?.account?.idAccount === idAccount)
        ) ?? null, [settings, idAccount]);

    const defaultPMInvalid = !defaultPM || !allowedPaymentTypes.includes(
      Number(defaultPM?.idPaymentType)
    );

    const isAutopayToggleDisabled = useMemo(() => {
        const { isAutoPay = false } = settings || {};
        return !((defaultPM && !defaultPM?.disabledAt && !defaultPMInvalid) || isAutoPay);
    }, [settings, allowedPaymentTypes, defaultPM, defaultPMInvalid]);

    const autopay = useMemo(() => {
        const { isAutoPay = false } = settings || {};
        return (
            <Toggle
                isOn={isAutoPay}
                onClick={toggleAutoPay}
                enabled={
                    (defaultPM && !defaultPM?.disabledAt && !defaultPMInvalid) ||
                    isAutoPay
                }
            />
        );
    }, [settings, allowedPaymentTypes]);

    const paymentDay = useMemo(() => {
        if (!orgDays) return null;
        const { isAutoPay = false, dueDay: payorDueDay = null, hasPOTInvoice } = settings || {};
        const { locked, autodrawDay, maxDay } = helpers.getDaysFromPayor(orgDays, payorDueDay, hasPOTInvoice ? Enums.ProductType.SunboundPay : null, potDeadline);

        return (
            settings && isAutoPay &&
            <PaymentDay setDueDay={dueDayChange} dueDay={autodrawDay} locked={locked} maxDay={maxDay} />
        );

    }, [settings]);

    // Filter out disabled payment methods
    const activePaymentMethods = useMemo(() =>
        (settings?.paymentMethods ?? []).filter((pm: PaymentMethod) => {
            const isDisabled =
                !!pm.disabledAt ||
                (!allowedPaymentTypes.includes(Number(pm?.idPaymentType ?? -1)) && !allowedPaymentTypesIsLoading);

            return !isDisabled;
        })
    , [settings?.paymentMethods, allowedPaymentTypes, allowedPaymentTypesIsLoading]);

    if (!settings) return null;

    const hasPaymentMethods = activePaymentMethods.length > 0;

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <IconButton
                        variant="tertiary"
                        size="sm"
                        onClick={() => navigate(-1)}
                        aria-label="Go back"
                        className="flex-shrink-0 md:btn-icon-lg"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px] truncate">
                        Payment Settings
                    </h1>
                </div>
                <Button
                    variant="primary"
                    size="md"
                    startIcon={<Plus />}
                    className="btn-icon-lg md:btn-text-md flex-shrink-0"
                    onClick={addDefaultPaymentMethod}
                    aria-label="Add Payment Method"
                >
                    <span className="hidden md:inline">Payment Method</span>
                </Button>
            </div>

            {/* Resident Selector */}
            {hasMultipleAccounts && (
                <div className="flex justify-end mb-6">
                    <ResidentSelector />
                </div>
            )}

            {/* Content */}
            <div className={`flex flex-col ${hasPaymentMethods ? `gap-8` : `gap-4`}`}>
                {/* Invalid Default Payment Method State */}
                {(defaultPM?.disabledAt || (!!defaultPM && defaultPMInvalid)) ? (
                    <p className="text-sm leading-5 md:text-base md:leading-6 text-neutral-700 tracking-[0.14px] md:tracking-[0.16px]">
                        {settings.isAutoPay
                            ? `Add a new default payment method to enable Autopay again and avoid late fees.`
                            : `Add a new default payment method to enable Autopay and avoid late fees.`}
                    </p>
                ) :
                /* No Default Payment Method State */
                (!hasPaymentMethods || !defaultPM) ? (
                    <p className="text-sm leading-5 md:text-base md:leading-6 text-neutral-700 tracking-[0.14px] md:tracking-[0.16px]">
                        Add a default payment method to enable Autopay and avoid late fees.
                    </p>
                ) :
                /* No issue to handle */
                undefined}

                {/* Payment Methods Section */}
                {hasPaymentMethods && (
                    <div className="flex flex-col gap-4">
                        <h2 className="text-base leading-6 md:text-xl md:leading-7 font-medium text-neutral-950 tracking-[0.16px] md:tracking-[0.2px]">
                            Payment Methods
                        </h2>
                        <div className="flex flex-col gap-4">
                            {activePaymentMethods.map((pm, idx) => (
                                <PaymentMethodCard
                                    idx={idx}
                                    key={pm.idPaymentMethod}
                                    paymentMethod={pm}
                                    isDefault={!!(pm && defaultPM && pm.idPaymentMethod === defaultPM.idPaymentMethod)}
                                    isDefaultOnOtherAccount={pm.accountPaymentMethods?.some(
                                        apm => apm.isDefault && apm.accountPayor?.account?.idAccount !== idAccount
                                    )}
                                    makeDefault={makeDefault}
                                    onDelete={deactivatePaymentMethod}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Autopay Section */}
                <div className={classNames(
                    `bg-white border rounded-xl p-4 flex flex-col gap-4`,
                    {
                        "border-neutral-100/40": isAutopayToggleDisabled,
                        "border-neutral-100": !isAutopayToggleDisabled
                    }
                )}>
                    <div className="flex items-center justify-between">
                        <span className={classNames(
                            `text-base leading-6 font-medium tracking-[0.32px]`,
                            {
                                "text-neutral-950/40": isAutopayToggleDisabled,
                                "text-neutral-950": !isAutopayToggleDisabled
                            }
                        )}>
                            Autopay
                        </span>
                        {autopay}
                    </div>

                    {/* Payment Day Selector (when autopay is on) */}
                    {settings.isAutoPay && paymentDay}
                </div>
            </div>
        </div>
    );
};

export default PaymentMethodSettings;
